// Pages/inventario/components/UnificarProductosModal.tsx
//
// «Estos son el mismo producto» — round 2026-08-06.
//
// La estandarización del texto y el buscador de recompra evitan que nazcan
// duplicados nuevos. Esto arregla los que YA existen.
//
// ── Por qué lo confirma una persona ─────────────────────────────────────────
//
// Agrupar automáticamente por nombre parecido uniría cosas que en realidad son
// distintas y mezclaría sus precios sin vuelta atrás. Un duplicado molesta; una
// unificación equivocada corrompe. La decisión es de quien conoce los productos.
//
// ── Qué hace y qué NO hace ──────────────────────────────────────────────────
//
// Une las FICHAS: qué es la cosa. El stock no se mueve ni se suma: cada lugar
// conserva sus unidades. Eso es exactamente lo que se busca: un producto en
// tres lugares, no tres productos. Decirlo explícito en el modal importa —
// "unificar" suena a fusionar stock, y quien lo entienda así va a dudar de sus
// números al día siguiente.
//
// El PRECIO sí se unifica, y por eso el modal lleva una segunda advertencia:
// desde «un producto, un precio» el promedio ponderado se rehace sobre las
// compras ya unidas y deja de haber un precio por ubicación.
import React, { useEffect, useState } from "react";
import { Alert, Modal, Radio, Space, Table, Tag, Typography } from "antd";
import type { Producto } from "../useInventarioLogic";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import "../inventario.css";

const { Text } = Typography;

interface Props {
  open: boolean;
  productos: Producto[];
  /** Ids seleccionados que NO están en la página actual (no se puede ver su ficha). */
  idsFueraDeVista: number[];
  confirmando: boolean;
  onCancel: () => void;
  onConfirm: (principalId: number) => void;
}

const UnificarProductosModal: React.FC<Props> = ({
  open,
  productos,
  idsFueraDeVista,
  confirmando,
  onCancel,
  onConfirm,
}) => {
  const [principal, setPrincipal] = useState<number | null>(null);

  // Firma estable de la selección. `productos` llega como un `.filter()` inline
  // del listado, o sea un ARRAY NUEVO en cada render del padre: usarlo de
  // dependencia hacía que el efecto corriera todo el tiempo y devolviera
  // `principal` al id más bajo. El admin elegía una ficha, cualquier re-render
  // (un evento de WebSocket, un toast) se la revertía en silencio, y confirmaba
  // una unificación IRREVERSIBLE con la ficha equivocada. Round 2026-08-06,
  // segunda revisión.
  const firma = productos.map((p) => p.id).join(",");

  useEffect(() => {
    // Por defecto, el de código más bajo: es el que existe hace más tiempo y,
    // en la práctica, el que ya tiene historia y fotos colgando. Solo se aplica
    // al abrir o si la selección misma cambió.
    if (!open || !firma) return;
    setPrincipal(Math.min(...firma.split(",").map(Number)));
  }, [open, firma]);

  const total = productos.length + idsFueraDeVista.length;

  return (
    <Modal
      open={open}
      title="¿Son el mismo producto?"
      okText="Sí, es un solo producto"
      cancelText="Cancelar"
      confirmLoading={confirmando}
      okButtonProps={{ disabled: total < 2 || principal === null }}
      onCancel={onCancel}
      onOk={() => principal !== null && onConfirm(principal)}
      width={760}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="El stock NO se mueve ni se suma"
        description={
          <>
            Cada lugar conserva sus unidades. Lo que se unifica es{" "}
            <strong>qué es la cosa</strong>: nombre, marca, color, material,
            categoría. A partir de acá, editar cualquiera de estas filas las
            edita a todas — porque pasan a ser un solo producto repartido en
            varios lugares.
          </>
        }
      />
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="El precio pasa a ser uno solo"
        description={
          <>
            Se recalcula como el <strong>promedio ponderado</strong> de todas las
            compras del producto, las de todos los lugares juntas. Si lo
            compraste a $5.290 en un lado y a $7.490 en otro, deja de haber dos
            precios.
            <br />
            <em>
              Esto no toca ni un peso de lo ya cobrado ni de lo ya gastado: cada
              compra conserva su monto real y los cobros a clientes salen de ahí.
              Lo que cambia es cuánto decimos que vale el stock.
            </em>
          </>
        }
      />

      {total < 2 ? (
        <Text type="warning">Hay que elegir al menos dos productos.</Text>
      ) : (
        <>
          <Text strong>¿Cuál es la ficha correcta?</Text>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Sus datos se copian a las demás. Las otras fichas se eliminan.
          </Text>

          <Radio.Group
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
            style={{ width: "100%" }}
          >
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={productos}
              columns={[
                {
                  title: "",
                  key: "elegir",
                  width: 40,
                  render: (_: unknown, p: Producto) => <Radio value={p.id} />,
                },
                {
                  title: "Producto",
                  key: "producto",
                  render: (_: unknown, p: Producto) => (
                    <Space size={6}>
                      {p.miniatura ? (
                        <img
                          className="inventario-miniatura-mini"
                          src={p.miniatura}
                          alt=""
                        />
                      ) : null}
                      <Tag className="inventario-id-tag">#{p.id}</Tag>
                      <span>{p.nombre}</span>
                      {p.marca ? <Text type="secondary">· {p.marca}</Text> : null}
                    </Space>
                  ),
                },
                {
                  title: "Lugar",
                  key: "lugar",
                  render: (_: unknown, p: Producto) => {
                    const lugar = String(p.ubicacion || "").split(" · ")[0] || "—";
                    return p.ubicacionTipo === "propiedades"
                      ? formatPropertyName(lugar)
                      : lugar;
                  },
                },
                {
                  title: "Stock",
                  key: "stock",
                  align: "right",
                  render: (_: unknown, p: Producto) => `${p.cantidadDeUnidades} u.`,
                },
              ]}
            />
          </Radio.Group>

          {idsFueraDeVista.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message={`${idsFueraDeVista.length} producto(s) seleccionado(s) fuera de esta página`}
              description={
                <>
                  También se unificarán: {idsFueraDeVista.map((id) => `#${id}`).join(", ")}.
                  No se pueden elegir como ficha principal porque no se ve qué
                  dicen — elegir a ciegas cuál manda es justo lo que no conviene
                  hacer acá.
                </>
              }
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default UnificarProductosModal;
